/**
 * SCC C2 data layer helpers — Customer Signals Platform (CSP) integration.
 *
 * Events are pushed to `window._signalsDataLayer`, which the SCC C2 client
 * (`scc-c2.min.js`) picks up and forwards to Event Bus. The client is loaded
 * in index.html along with `_trfd` (page-level config with `ap` app name).
 *
 * Element identifier (A-Schema): `{area}.{product}.{section}.{widget}`
 *
 * - `area`    — Team identifier, constant per team ('parking')
 * - `product` — App identifier, constant per app ('lander')
 * - `section` — Page section where the interaction occurred (varies per event)
 * - `widget`  — Specific element interacted with (varies per event)
 *
 * All events are gated behind `?scc=1` query parameter via `isSccEnabled()`.
 *
 * @see https://godaddy-corp.atlassian.net/wiki/spaces/CKPT/pages/92315377/_signalsDataLayer+current
 */

/** An event pushed to the SCC C2 data layer */
interface SignalsDataLayerEvent {
  schema: string;
  data: Record<string, unknown>;
}

/** Page-level traffic config entry consumed by the SCC C2 client */
interface TrfdConfig {
  /** Application identifier (e.g. "parking") */
  ap: string;
}

declare global {
  interface Window {
    _trfd: TrfdConfig[];
    _signalsDataLayer: SignalsDataLayerEvent[];
  }
}

const AREA = "parking";
const PRODUCT = "lander";

interface ElementIdentifier {
  section: string;
  widget: string;
}

function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window._signalsDataLayer = window._signalsDataLayer || [];
}

export function isSccEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("scc") === "1";
}

export function trackImpression(element: ElementIdentifier): void {
  if (!isSccEnabled()) return;
  ensureDataLayer();
  window._signalsDataLayer.push({
    schema: "add_impressions",
    data: {
      impressions: [
        {
          element: {
            area: AREA,
            product: PRODUCT,
            ...element,
          },
        },
      ],
    },
  });
}

export function trackClick(element: ElementIdentifier): void {
  if (!isSccEnabled()) return;
  ensureDataLayer();
  window._signalsDataLayer.push({
    schema: "add_click",
    data: {
      element: {
        area: AREA,
        product: PRODUCT,
        ...element,
      },
    },
  });
}
